// ============================================================================
// permissionCenter.ts — ЕДИНЫЙ центр разрешений клиента (SSoT/SRP).
//
// Реестр ВСЕХ разрешений платформы (браузерные + серверная политика):
//   push          — Web Push (подписка; запрос = requestPushPermission по клику)
//   notifications — Notification.requestPermission (грант на уведомления)
//   geolocation   — navigator.geolocation (запрос = captureLocation)
//   camera        — getUserMedia({video}) — «Камера» (снимки/видеозвонки)
//   microphone    — getUserMedia({audio}) — голосовые/STT
//   tracking      — ОТСЛЕЖИВАНИЕ КООРДИНАТ (серверная политика роли:
//                   user_settings location.track + LocationService.TrackingStatus)
//
// Состояние каждого разрешения: 'granted' | 'denied' | 'default' | 'unsupported'
// (unsupported — средство/API недоступно, например query('camera') в WebView2).
// Запрос (request) — единственная точка, БЕЗ фоллбэков; для браузерных
// permissions «безусловный запрос» = фактический вызов API (вызывает системный
// диалог по месту). Все ошибки пробрасываются/логируются — никаких тихих catch.
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { captureLocation } from '@/lib/geolocation';
import { isPushSupported, subscribePush, unsubscribePush } from '@/lib/swUpdate';
import { createLogger } from '@/lib/logger';

const log = createLogger('permissionCenter');

export type PermissionState = 'granted' | 'denied' | 'default' | 'unsupported';
export type PermissionKind = 'push' | 'notifications' | 'geolocation' | 'camera' | 'microphone' | 'tracking';

export interface PermissionDef {
  kind: PermissionKind;
  label: string;
  description: string;
  /** Признак: разрешение уже выдано (для UI-состояния) */
  granted?: boolean;
}

// Связанные ключи query() браузерного Permissions API (там, где поддерживается)
const PERM_QUERY_NAME: Partial<Record<PermissionKind, PermissionName>> = {
  geolocation: 'geolocation',
  notifications: 'notifications',
  camera: 'camera',
  microphone: 'microphone',
  push: 'notifications',
};

async function queryState(name: PermissionName): Promise<PermissionState> {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return 'unsupported';
  try {
    const s = await navigator.permissions.query({ name });
    return s.state as PermissionState;
  } catch {
    // name не поддерживается данным браузером/WebView2 → явное состояние
    return 'unsupported';
  }
}

function notificationState(): PermissionState {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission as PermissionState;
}

/** push: granted только если подписка ЕСТЬ (endpoint на сервере). Разрешение
 *  Notification при этом может быть granted, а подписки нет (не подписан). */
async function pushState(): Promise<PermissionState> {
  if (!isPushSupported()) return 'unsupported';
  const notif = notificationState();
  if (notif === 'denied') return 'denied';
  if (notif === 'unsupported') return 'unsupported';
  try {
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();
    if (sub) return 'granted';
  } catch (e: any) {
    log.warn('push state check failed', e);
  }
  // Разрешение дано, но подписки нет — «default» (нужно действие)
  return notif === 'granted' ? 'default' : notif;
}

async function trackingState(): Promise<PermissionState> {
  try {
    const r: any = await getSDK().location.getMyTrackingStatus({});
    if (r?.mode === 'off') return 'denied';
    if (r?.unconditional || r?.enabled) return 'granted';
    return 'default';
  } catch (e: any) {
    log.warn('tracking status unavailable', e);
    return 'unsupported';
  }
}

export interface PermissionSnapshot {
  kind: PermissionKind;
  state: PermissionState;
  label: string;
  description: string;
}

const LABELS: Record<PermissionKind, { label: string; description: string }> = {
  push: { label: 'Уведомления (push)', description: 'Web Push: уведомления о событиях, даже когда приложение закрыто' },
  notifications: { label: 'Уведомления', description: 'Браузерное разрешение на показ уведомлений' },
  geolocation: { label: 'Геолокация', description: 'Определение координат устройства (файлы/отслеживание)' },
  camera: { label: 'Камера', description: 'Захват с камеры (снимки, видеозвонки)' },
  microphone: { label: 'Микрофон', description: 'Голосовые сообщения, диктовка, звонки' },
  tracking: { label: 'Отслеживание координат', description: 'Серверная политика роли: передача геопозиции пользователя' },
};

const DEFAULT_ORDER: PermissionKind[] = ['notifications', 'push', 'geolocation', 'camera', 'microphone', 'tracking'];

export function permissionLabel(kind: PermissionKind): string {
  return LABELS[kind].label;
}

export function permissionDescription(kind: PermissionKind): string {
  return LABELS[kind].description;
}

/** Получить СОСТОЯНИЕ одного разрешения (SSoT: браузерные query()/Notification;
 *  tracking — сервер). Без фоллбэков — недоступное средство → 'unsupported'. */
export async function getPermissionState(kind: PermissionKind): Promise<PermissionState> {
  switch (kind) {
    case 'push':
      return pushState();
    case 'notifications':
      return notificationState();
    case 'geolocation':
    case 'camera':
    case 'microphone': {
      const qname = PERM_QUERY_NAME[kind];
      if (!qname) return 'unsupported';
      const q = await queryState(qname);
      if (q !== 'unsupported') return q;
      // Permissions API не знает 'camera'/'microphone' — но в браузере это
      // управляется тем же механизмом, что geolocation: probe отсутствует.
      // Для геолокации используем явную проверку (navigator.geolocation).
      if (kind === 'geolocation') return typeof navigator !== 'undefined' && !!navigator.geolocation ? 'default' : 'unsupported';
      return 'default';
    }
    case 'tracking':
      return trackingState();
  }
}

/** Состояния ВСЕХ разрешений (для центрального экрана). */
export async function getAllPermissionStates(kinds: PermissionKind[] = DEFAULT_ORDER): Promise<PermissionSnapshot[]> {
  const out: PermissionSnapshot[] = [];
  for (const kind of kinds) {
    let state: PermissionState = 'unsupported';
    try {
      state = await getPermissionState(kind);
    } catch (e: any) {
      log.warn('permission state failed: ' + kind, e);
      state = 'unsupported';
    }
    out.push({ kind, state, label: LABELS[kind].label, description: LABELS[kind].description });
  }
  return out;
}

/**
 * ЗАПРОСИТЬ разрешение (единственная точка; вызывается ПО КЛИКУ пользователя —
 * системные диалоги требуют user gesture). Возвращает итоговое состояние.
 * push/notifications — Notification.requestPermission + subscribePush;
 * geolocation — captureLocation (сам вызов = запрос);
 * camera/microphone — getUserMedia (открывает системный диалог);
 * tracking — согласие на сервере (setMyTracking on); unconditional/off — сервер
 * отклоняет, ошибка пробрасывается (нельзя запросить то, что запрещено).
 */
export async function requestPermission(kind: PermissionKind): Promise<PermissionState> {
  switch (kind) {
    case 'push': {
      if (!isPushSupported()) return 'unsupported';
      const m = await import('@/lib/swUpdate');
      if (typeof Notification === 'undefined') return 'unsupported';
      if (Notification.permission === 'denied') return 'denied';
      if (Notification.permission !== 'granted') {
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') return perm as PermissionState;
      }
      const pub = String((await getSDK().push.getVapidKey({}))?.publicKey || '');
      const ok = await m.subscribePush(pub, window.location.origin);
      return ok ? 'granted' : 'default';
    }
    case 'notifications': {
      if (typeof Notification === 'undefined') return 'unsupported';
      if (Notification.permission === 'granted') return 'granted';
      const perm = await Notification.requestPermission();
      return perm as PermissionState;
    }
    case 'geolocation': {
      if (typeof navigator === 'undefined' || !navigator.geolocation) return 'unsupported';
      try {
        await captureLocation();
        return 'granted';
      } catch (e: any) {
        // Отказ пользователя/недоступность: детерминированно читаем состояние
        log.warn('geolocation request failed', e);
        const st = await queryState('geolocation');
        return st === 'unsupported' ? 'denied' : st;
      }
    }
    case 'camera': {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach((t) => t.stop());
        return 'granted';
      } catch {
        const st = await queryState('camera');
        return st === 'unsupported' ? 'denied' : st;
      }
    }
    case 'microphone': {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
        return 'granted';
      } catch {
        const st = await queryState('microphone');
        return st === 'unsupported' ? 'denied' : st;
      }
    }
    case 'tracking': {
      const r: any = await getSDK().location.setMyTracking({ enabled: 'on' });
      if (r?.enabled) return 'granted';
      if (r?.unconditional) return 'granted';
      return 'default';
    }
  }
}

/** Снять разрешение (где возможно): tracking — сервер (off); push — отписка.
 *  geolocation/camera/microphone/notifications браузер отзывает сам —
 *  здесь только ВОЗВРАЩАЕМ актуальное состояние (управление — в браузере). */
export async function revokePermission(kind: PermissionKind): Promise<PermissionState> {
  switch (kind) {
    case 'tracking': {
      const r: any = await getSDK().location.setMyTracking({ enabled: 'off' });
      return r?.enabled ? 'granted' : 'default';
    }
    case 'push': {
      await unsubscribePush();
      return 'default';
    }
    default:
      return getPermissionState(kind);
  }
}
